// apps/office/src/pages/Reports.jsx
import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription 
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  DollarSign, 
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  Download,
  RefreshCw,
  Eye,
  Clock
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';

const Reports = () => {
  const [dateRange, setDateRange] = useState('this-month');
  const [activeTab, setActiveTab] = useState('overview');

  // Mock metrics data (will be replaced with real data later)
  const metrics = {
    totalClients: {
      value: '1,284',
      change: '+12.5%',
      trend: 'up',
      label: 'Total Clients',
      icon: Users,
      color: 'text-indigo-500',
      bgColor: 'bg-indigo-50'
    },
    totalRevenue: {
      value: '?48.2L',
      change: '+8.3%',
      trend: 'up',
      label: 'Total Revenue',
      icon: DollarSign,
      color: 'text-green-500',
      bgColor: 'bg-green-50'
    },
    conversionRate: {
      value: '24.8%',
      change: '-2.1%',
      trend: 'down',
      label: 'Conversion Rate',
      icon: PieChart,
      color: 'text-amber-500',
      bgColor: 'bg-amber-50'
    },
    activeProjects: {
      value: '142',
      change: '+5.7%',
      trend: 'up',
      label: 'Active Projects',
      icon: BarChart3,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50'
    }
  };

  // Mock recent activities
  const recentActivities = [
    { id: 1, type: 'lead', title: 'New property enquiry', description: 'John Doe enquired about Hillside Villa', time: '5 min ago', status: 'new' },
    { id: 2, type: 'deal', title: 'Deal closed', description: 'Premium flat sold to Priya Sharma', time: '1 hour ago', status: 'closed' },
    { id: 3, type: 'client', title: 'New client registered', description: 'Rajesh Kumar created an account', time: '3 hours ago', status: 'active' },
    { id: 4, type: 'payment', title: 'Payment received', description: '?50,000 advance payment for property #234', time: '5 hours ago', status: 'completed' },
  ];

  const MetricCard = ({ metric }) => {
    const Icon = metric.icon;
    const isUp = metric.trend === 'up';
    
    return (
      <Card className="shadow-sm border-slate-200 hover:shadow-md transition-shadow duration-200">
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-slate-500 font-medium">{metric.label}</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{metric.value}</p>
              <div className="flex items-center gap-1 mt-1">
                {isUp ? (
                  <ArrowUpRight className="w-4 h-4 text-green-500" />
                ) : (
                  <ArrowDownRight className="w-4 h-4 text-red-500" />
                )}
                <span className={`text-sm font-medium ${isUp ? 'text-green-500' : 'text-red-500'}`}>
                  {metric.change}
                </span>
                <span className="text-xs text-slate-400">vs last month</span>
              </div>
            </div>
            <div className={`p-3 rounded-full ${metric.bgColor}`}>
              <Icon className={`w-5 h-5 ${metric.color}`} />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Reports & Analytics</h1>
          <p className="text-slate-500 text-sm mt-1">
            Track performance, revenue, and client insights at a glance
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[160px] h-9">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Select range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="this-week">This Week</SelectItem>
              <SelectItem value="this-month">This Month</SelectItem>
              <SelectItem value="this-quarter">This Quarter</SelectItem>
              <SelectItem value="this-year">This Year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="gap-2">
            <Download className="w-4 h-4" />
            Export
          </Button>
          <Button variant="outline" size="sm" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.values(metrics).map((metric, index) => (
          <MetricCard key={index} metric={metric} />
        ))}
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="clients">Clients</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Chart Placeholder */}
            <Card className="shadow-sm border-slate-200 col-span-1 lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-indigo-500" />
                  Revenue Overview
                </CardTitle>
                <CardDescription>Monthly revenue trends for the current year</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64 bg-slate-50 rounded-lg flex items-center justify-center border-2 border-dashed border-slate-200">
                  <div className="text-center">
                    <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-400">Chart will appear here</p>
                    <p className="text-xs text-slate-300">Real-time data integration coming soon</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card className="shadow-sm border-slate-200 col-span-1 lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="w-4 h-4 text-indigo-500" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {recentActivities.map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0 last:pb-0">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-slate-800 truncate">
                          {activity.title}
                        </p>
                        <Badge variant="outline" className="text-xs capitalize">
                          {activity.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 truncate">{activity.description}</p>
                      <p className="text-xs text-slate-400 mt-1">{activity.time}</p>
                    </div>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0">
                      <Eye className="w-4 h-4 text-slate-400" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card className="shadow-sm border-slate-200 col-span-1 lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-indigo-500" />
                  Quick Stats
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  { label: 'Total Properties', value: '45', change: '+3' },
                  { label: 'Active Listings', value: '28', change: '+5' },
                  { label: 'Pending Deals', value: '12', change: '-2' },
                  { label: 'New Leads This Month', value: '67', change: '+18' },
                ].map((stat, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                    <span className="text-sm text-slate-600">{stat.label}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-slate-800">{stat.value}</span>
                      <Badge variant={stat.change.startsWith('+') ? 'default' : 'secondary'} className="text-xs">
                        {stat.change}
                      </Badge>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="clients">
          <Card className="shadow-sm border-slate-200">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="w-4 h-4 text-indigo-500" />
                Client Analytics
              </CardTitle>
              <CardDescription>Client demographics and engagement metrics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64 bg-slate-50 rounded-lg flex items-center justify-center border-2 border-dashed border-slate-200">
                <div className="text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">Client analytics dashboard</p>
                  <p className="text-xs text-slate-300">Coming soon with real data</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance">
          <Card className="shadow-sm border-slate-200">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <PieChart className="w-4 h-4 text-indigo-500" />
                Performance Metrics
              </CardTitle>
              <CardDescription>Conversion rates, response times, and team performance</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64 bg-slate-50 rounded-lg flex items-center justify-center border-2 border-dashed border-slate-200">
                <div className="text-center">
                  <PieChart className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">Performance analytics dashboard</p>
                  <p className="text-xs text-slate-300">Coming soon with real data</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Footer Note */}
      <p className="text-xs text-slate-400 text-center border-t border-slate-200 pt-4">
        Reports are updated in real-time. Data is sourced from PocketBase collections.
        <span className="block mt-1 text-slate-300">Last updated: {new Date().toLocaleString('en-IN')}</span>
      </p>
    </div>
  );
};

export default Reports;